import { Area } from 'react-easy-crop';

/**
 * Loads an image from a blob/object URL.
 */
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    // Needed to avoid CORS issues when the source is a remote URL (data/blob URLs are unaffected).
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

/**
 * Crops `imageSrc` to the pixel area described by `cropArea` (as produced by react-easy-crop's
 * onCropComplete) and returns the result as a File, preserving the original mime type/name where
 * possible so it uploads just like a normal picked file.
 */
export async function getCroppedImageFile(
  imageSrc: string,
  cropArea: Area,
  fileName: string,
  mimeType: string = 'image/jpeg'
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(cropArea.width));
  canvas.height = Math.max(1, Math.round(cropArea.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  ctx.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height
  );

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error('Canvas is empty'));
      },
      mimeType,
      0.92
    );
  });

  return new File([blob], fileName, { type: mimeType });
}
